import { useState, useEffect } from 'react';
import { VStack, HStack, Text, Box } from 'native-base';

// Assets
import { theme } from '@breakroom-nx/assets';

// Classes & Types
import {
  Feedback,
  User,
  getUserData,
  AppFeedbackType,
  ImplementationState,
} from '@breakroom-nx/classes';
import {
  UserAvatar,
  VotingButton,
  FeedbackType,
  CommentCell,
  TableView,
  TextView,
  IconButton,
} from '../../';
import {
  useVoteOnFeedbackMutation,
  useGetCurrentUserQuery,
  useLazyGetFeedbackCommentsQuery,
  useCreateCommentMutation,
} from '@breakroom-nx/redux';
import { QueryStatus } from '@reduxjs/toolkit/dist/query';
import { timeFrom, InputType } from '@breakroom-nx/helpers';
import { PaperAirplaneIcon } from 'react-native-heroicons/solid';
import { PencilIcon } from 'react-native-heroicons/outline';
import { ChatIcon } from 'react-native-heroicons/solid';

import * as Yup from 'yup';
import { Formik, ErrorMessage } from 'formik';

export interface FeedbackCellProps {
  data: Feedback;
  type: AppFeedbackType;
  disableComments?: boolean;
}

const CommentScheme = Yup.object().shape({
  comment: Yup.string().max(200, 'Maximum of 200 characters').optional(),
});

interface CommentFormValues {
  comment: string;
}

export const FeedbackCell = ({
  data,
  type,
  disableComments = false,
}: FeedbackCellProps) => {
  const { data: currentUser } = useGetCurrentUserQuery();
  const [
    trigger,
    { data: comments, isLoading: isLoadingComments, isSuccess, isError, error },
  ] = useLazyGetFeedbackCommentsQuery();

  const [vote, voteState] = useVoteOnFeedbackMutation();
  const [uploadComment, updatedComment] = useCreateCommentMutation();

  const [isChanged, setIsChanged] = useState(false);
  const [creator, setCreator] = useState<User | null>();
  const [feedback, setFeedback] = useState(FeedbackType.None);

  // For Comments
  const [comment, setComment] = useState('');
  const [showComments, setShowComments] = useState(false);
  const [showTextField, setShowTextField] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // For Submitting Comments
  const handleCommentSubmit = async () => {
    setIsSubmitting(true);
    // TODO: Replace with actual comment submission logic
    // console.log('Submitted comment:', comment);
    setComment('');
    setIsSubmitting(false);
  };

  const initialValues: CommentFormValues = {
    comment: '',
  };

  const displayComments = () => {
    return comments?.map((data) => {
      return <CommentCell data={data} key={data.uid} />;
    });
  };

  const getColor = () => {
    if (data) {
      switch (data.implementationState as ImplementationState) {
        case ImplementationState.Reviewed:
          return theme.colors.feedback.reviewed;
        case ImplementationState.InDevelopment:
          return theme.colors.feedback.inDevelopment;
        case ImplementationState.Live || ImplementationState.Fixed:
          return theme.colors.feedback.live;
        case ImplementationState.Backlog:
          return theme.colors.feedback.backlog;
        case ImplementationState.Bug:
          return theme.colors.feedback.bug;
        default:
          return theme.colors.feedback.default;
      }
    } else {
      return theme.colors.feedback.default;
    }
  };

  const modifyVote = (upvote: boolean) => {
    // console.log(`Vote Made: ${upvote}`);
    if (currentUser) {
      vote({ user: currentUser, feedbackID: data.uid, type, upvote })
        .unwrap()
        .then(() => {
          if (upvote) {
            if (feedback === FeedbackType.Praise) {
              setFeedback(FeedbackType.None);
            } else {
              setFeedback(FeedbackType.Praise);
            }
          } else {
            if (feedback === FeedbackType.Scold) {
              setFeedback(FeedbackType.None);
            } else {
              setFeedback(FeedbackType.Scold);
            }
          }
        })
        .catch((err) => {
          // Throw Error
        });
    }
  };

  const updateCreatorData = async () => {
    const userData = await getUserData(data.creatorUID!);
    setCreator(userData);
  };

  useEffect(() => {
    if (data.creatorUID !== undefined) {
      updateCreatorData();
    }
  }, []);

  return (
    <VStack
      space={5}
      alignItems={'start'}
      alignContent={'stretch'}
      rounded={'lg'}
      p={5}
      mb={8}
      bg={theme.colors.surface[200]}
    >
      <HStack
        alignItems={'center'}
        justifyContent={'space-between'}
        alignContent={'space-between'}
        width={'full'}
      >
        {data.creatorUID ? (
          <HStack space={3} alignItems={'center'}>
            <UserAvatar uid={data.creatorUID} size={8} />
            <Text
              fontSize={16}
              variant={'usernameProfile'}
              color={creator?.theme ?? theme.colors.white}
              isTruncated
            >
              {creator?.username}
            </Text>
          </HStack>
        ) : null}
        <Box
          textAlign={'center'}
          rounded={'full'}
          borderRadius={'full'}
          borderColor={getColor()}
          px={'2'}
          py={'0.5'}
          borderWidth={1.5}
          style={{
            backgroundColor: `rgba(0,0,0,0.15)`,
          }}
        >
          <Text color={getColor()} fontWeight={'bold'}>
            {data.implementationState}
          </Text>
        </Box>
      </HStack>

      <Text color={'white'}>{data.content}</Text>
      <HStack
        width={'full'}
        alignItems={'center'}
        justifyContent={'space-between'}
      >
        <Text color={theme.colors.surface[500]} fontSize={14} fontWeight={400}>
          {timeFrom(data.createdAt)}
        </Text>
        <HStack space={3}>
          {!disableComments && (
            <HStack space={3}>
              <IconButton
                icon={
                  <PencilIcon size={'20px'} color={theme.colors.yellow[400]} />
                }
                onPress={() => {
                  setShowTextField(!showTextField);
                }}
                tooltipText={`${showTextField ? 'Hide' : 'Show'} Text Field`}
              />
              <IconButton
                icon={
                  <ChatIcon size={'20px'} color={theme.colors.yellow[400]} />
                }
                disabled={isLoadingComments}
                onPress={() => {
                  setShowComments(!showComments);
                  if (!showComments) {
                    trigger({ uid: data.uid });
                  }
                }}
                tooltipText={`${showComments ? 'Hide' : 'View'} Comments`}
              />
            </HStack>
          )}
          <VotingButton
            voteState={feedback}
            updateVotes={modifyVote}
            voteCount={data.feedback.upvotes}
            disabled={!currentUser || voteState.status === QueryStatus.pending}
            upvote
          />
          <VotingButton
            voteState={feedback}
            updateVotes={modifyVote}
            voteCount={data.feedback.downvotes}
            disabled={!currentUser || voteState.status === QueryStatus.pending}
            upvote={false}
          />
        </HStack>
      </HStack>
      {showTextField && (
        <Formik
          initialValues={initialValues}
          validationSchema={CommentScheme}
          onSubmit={async (values, { setSubmitting }) => {
            // console.log(`Values Comment: ${values.comment}]`);

            await uploadComment({
              user: currentUser,
              feedbackID: data.uid,
              comment: values.comment,
            })
              .then(() => {
                // console.log(`Comment Uploaded!`);
              })
              .catch(() => {
                // console.log(`Issue Submitting Comment`);
              });
          }}
        >
          {({
            values,
            isSubmitting,
            handleChange,
            handleBlur,
            handleSubmit,
          }) => {
            // Track changes to the form fields and image
            useEffect(() => {
              const hasChanges = values.comment !== initialValues.comment;
              setIsChanged(hasChanges);
            }, [values]);

            return (
              <HStack
                space={8}
                width={'full'}
                bgColor={theme.colors.surface[300]}
                rounded={'lg'}
                alignItems={'end'}
                justifyContent={'end'}
              >
                <VStack space={2} flex={1}>
                  <TextView
                    disabled={isSubmitting}
                    type={InputType.Text}
                    value={values.comment}
                    onChangeText={handleChange('comment')}
                    onBlur={handleBlur('comment')}
                    isRequired={true}
                    inactiveTextColor={'surface.500'}
                    placeholder={'Leave a comment'}
                  />
                  <Text variant={'error'}>
                    <ErrorMessage name="comment" />
                  </Text>
                </VStack>
                <IconButton
                  icon={
                    <PaperAirplaneIcon
                      size={'20px'}
                      color={theme.colors.primary[400]}
                      style={{
                        transform: [{ rotate: '45deg' }],
                      }}
                    />
                  }
                  disabled={
                    !isChanged || updatedComment.status === QueryStatus.pending
                  }
                  onPress={handleSubmit}
                  tooltipText={'Send Comment'}
                />
              </HStack>
            );
          }}
        </Formik>
      )}
      {comments && showComments && (
        <TableView
          isLoading={isLoadingComments}
          isSuccess={isSuccess}
          isError={isError}
          error={error}
          noData={!comments || comments.length < 1}
          noDataString={`No Comments Yet`}
          px={0}
          pb={0}
          height={'1/2'}
          onRefetch={() => {}}
        >
          {displayComments()}
        </TableView>
      )}
    </VStack>
  );
};
